import {useCallback, useMemo, useRef} from "react";
import {MobileTabs, MobileGroupName} from "../data";
import {useRecoilState, useRecoilValue} from "recoil";
import {stateMobileDrawerIsActive, stateMobileDrawerTabId, stateComplectationBlocks} from "../data/atoms";
import Typecheck from "../utils/Typecheck";
import {useSwipeable} from "react-swipeable";
import IconMobileSize from "../components/Icons/IconMobileSize";
import IconMobileOsnova from "../components/Icons/IconMobileOsnova";
import IconMobileMeshok from "../components/Icons/IconMobileMeshok";
import IconMobileRemen from "../components/Icons/IconMobileRemen";
import IconMobileAccessories from "../components/Icons/IconMobileAccessories";
import {doScrolling, isPinsBlockId} from "../utils/ApplicationUtils";


const icons = {
    [MobileGroupName.sizes]: IconMobileSize,
    [MobileGroupName.osnova]: IconMobileOsnova,
    [MobileGroupName.osnovaRemen]: IconMobileOsnova,
    [MobileGroupName.meshok]: IconMobileMeshok,
    [MobileGroupName.remen]: IconMobileRemen,
    [MobileGroupName.accessories]: IconMobileAccessories,
};

const useGetMobileTabs = () => {
    const complectations = useRecoilValue(stateComplectationBlocks);

    return useMemo(() => {
        const groups: MobileTabs[] = [];

        if (complectations.length) {

            groups.push({
                id: MobileGroupName.sizes,
                title: 'Размер',
                icon: icons[MobileGroupName.sizes],
            });

            complectations.forEach((block) => {
                /**
                 * Pins are one of base elements
                 * and do not need separated tab
                 */
                const isPins = isPinsBlockId(block.id);

                if (!isPins && Typecheck.isEnumValue(block.id, MobileGroupName)) {
                    groups.push({
                        id: block.id,
                        title: block.title,
                        icon: icons[block.id] || IconMobileOsnova,
                    });
                }
            });

            groups.push({
                id: MobileGroupName.accessories,
                title: 'Аксессуары',
                icon: icons[MobileGroupName.accessories],
            });
        }

        return groups;
    }, [complectations]);
}

export const useMobileTabs = (isLoading: boolean) => {
    const [isDrawerActive, setIsDrawerActive] = useRecoilState(stateMobileDrawerIsActive);
    const [activeTabId, setActiveTabId] = useRecoilState(stateMobileDrawerTabId);

    /**
     * This ref is used for scrolling to the builder container
     * on tab opening
     */
    const wrapReference = useRef<HTMLDivElement>(null);

    /**
     * This ref is used for detecting size of block with builder models
     * to slide content up on tab opening
     */
    const headReference = useRef<HTMLDivElement>(null);

    /**
     * List of mobile tabs
     * Contains sizes, accessories and list of complectations depended on API-response
     */
    const tabs = useGetMobileTabs();


    const openTab = useCallback((tabId: string) => {
        if (Typecheck.isEnumValue(tabId, MobileGroupName)) {
            (async () => {
                if (activeTabId === null && wrapReference.current) {
                    await doScrolling(wrapReference.current.offsetTop, 500);
                }

                setIsDrawerActive(true);
                setActiveTabId(tabId);
            })();
        }
    }, [activeTabId, setActiveTabId, setIsDrawerActive]);

    const closeTab = useCallback(() => {
        setIsDrawerActive(false);
    }, [setIsDrawerActive]);

    const toggleTab = useCallback((tabId: string | null) => {
        if (Typecheck.isEnumValue(tabId, MobileGroupName)) {
            if (isDrawerActive && activeTabId === tabId) {
                closeTab();
            } else {
                openTab(tabId);
            }
        }
    }, [activeTabId, closeTab, isDrawerActive, openTab]);


    /**
     * Swipe actions controller
     * for builder overlay
     */
    const swipeOverlay = useSwipeable({
        preventScrollOnSwipe: true,
        onSwipedDown: () => {
            if (!isLoading && isDrawerActive) {
                closeTab();
            }
        },
    });

    /**
     * Swipe actions controller
     * for mobile tabs panel
     */
    const swipeHandlers = useSwipeable({
        preventScrollOnSwipe: true,

        onSwipedUp: () => {
            if (!isLoading && !isDrawerActive) {
                openTab(activeTabId || tabs[0].id);
            }
        },
        onSwipedDown: () => {
            if (!isLoading && isDrawerActive) {
                closeTab();
            }
        },
        onSwipedLeft: () => {
            if (!isLoading && isDrawerActive) {
                let tabId = tabs[0].id;

                tabs.forEach((tab, index, tabs) => {
                    if (tab.id === activeTabId) {
                        tabId = (index === tabs.length - 1)
                            ? tabs[0].id
                            : tabs[index + 1].id;

                        return;
                    }
                });

                openTab(tabId);
            }
        },
        onSwipedRight: () => {
            if (!isLoading && activeTabId) {
                let tabId = tabs[0].id;

                tabs.forEach((tab, index, tabs) => {
                    if (tab.id === activeTabId) {
                        tabId = (index === 0)
                            ? tabs[tabs.length - 1].id
                            : tabs[index - 1].id;

                        return;
                    }
                });

                openTab(tabId);
            }
        }
    });


    return {
        tabs,
        isDrawerActive,
        activeTabId,
        openTab,
        closeTab,
        toggleTab,
        swipeOverlay,
        swipeHandlers,
        wrapReference,
        headReference,
    };
};
